import type { FormFieldInterface } from "../dom/form";

export class FormField implements FormFieldInterface {
  private hiddenOptions = new Set<string>();
  private hiddenOptionsStyle: HTMLStyleElement | null = null;
  private filterStyle: HTMLStyleElement | null = null;

  constructor(
    private readonly id: string | number,
    private readonly type: "system" | "custom",
    private readonly container: HTMLElement | Document = document,
  ) {}

  get prefix(): string {
    switch (this.type) {
      case "system":
        return "request_";
      case "custom":
        return "request_custom_fields_";
    }
  }

  elements() {
    const field = this;
    return {
      get input(): HTMLInputElement | null {
        return field.container.querySelector(`#${field.prefix}${field.id}`);
      },
      get nestyInput(): HTMLAnchorElement | null {
        return field.container.querySelector(
          `.${field.prefix}${field.id} .nesty-input`,
        );
      },
      get nestyPanel(): readonly Element[] | null {
        return this.nestyInput?.ariaControlsElements ?? null;
      },
      get label(): HTMLLabelElement | null {
        return field.container.querySelector(
          `label[for="${field.prefix}${field.id}"]`,
        );
      },
      get parent(): HTMLDivElement | null {
        return field.container.querySelector(`.${field.prefix}${field.id}`);
      },
    };
  }

  show(): void {
    this.elements().parent?.style.setProperty("display", "");
  }

  hide(): void {
    this.elements().parent?.style.setProperty("display", "none");
  }

  hideOptions(values: string | string[]): void {
    [values].flat().forEach((v) => this.hiddenOptions.add(v));
    this.applyHiddenOptionsCSS();
  }

  showOptions(values: string | string[]): void {
    [values].flat().forEach((v) => this.hiddenOptions.delete(v));
    this.applyHiddenOptionsCSS();
  }

  resetOptions(): void {
    this.hiddenOptions.clear();
    this.hiddenOptionsStyle?.remove();
    this.hiddenOptionsStyle = null;
  }

  filterOptions(values: string | string[]): void {
    const controls = this.getControls();
    if (!controls) return;

    const selector = [values].flat().map((v) => `[id="${v}"]`).join(",");

    this.filterStyle?.remove();
    this.filterStyle = document.createElement("style");
    this.filterStyle.innerText = `#${controls.id} li:not(:first-child,${selector}) { display: none !important; }`;
    document.head.append(this.filterStyle);
  }

  clearFilter(): void {
    this.filterStyle?.remove();
    this.filterStyle = null;
  }

  private getControls(): Element | null {
    const nestyInput = this.elements().nestyInput;
    if (!nestyInput) {
      console.warn(`FormField: nesty-input not found for field "${this.id}"`);
      return null;
    }
    return nestyInput.ariaControlsElements?.[0] ?? null;
  }

  private applyHiddenOptionsCSS(): void {
    this.hiddenOptionsStyle?.remove();

    if (this.hiddenOptions.size === 0) {
      this.hiddenOptionsStyle = null;
      return;
    }

    const controls = this.getControls();
    if (!controls) return;

    const selector = [...this.hiddenOptions]
      .map((v) => `[id="${v}"]`)
      .join(",");

    this.hiddenOptionsStyle = document.createElement("style");
    this.hiddenOptionsStyle.innerText = `#${controls.id} :is(${selector}) { display: none !important; }`;
    document.head.append(this.hiddenOptionsStyle);
  }
}
